import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';

import { RECEIVE_ATTACHMENT_TYPES } from '@/entities/receive';
import { AttachmentUploadModal } from '@/features/consignment-attachment-upload';
import { ShowDispositionDialog } from '@/features/show-disposition';
import { MANUAL_NODES } from '@/shared/config/manual';
import { DataTable } from '@/shared/ui/data-table';

import { useAttachmentUploadDialog, useReceiveHistory } from '../model';
import { getReceiveHistoryColumns, getReceiveHistoryColumnPresets, buildReceiveHistoryRowActions } from '../model';

export function ReceiveHistoryPage() {
  const { t } = useTranslation();
  const [dispositionConsignmentId, setDispositionConsignmentId] = useState<string | null>(null);
  const {
    tableData,
    totalElements,
    isLoading,
    isFetchingNextPage,
    error,
    isValid,
    hasMore,
    handleLoadMore,
    sortBy,
    sortDirection,
    handleSort
  } = useReceiveHistory();

  const attachmentDialog = useAttachmentUploadDialog();

  const columns = useMemo(() => getReceiveHistoryColumns(t), [t]);
  const columnPresets = useMemo(() => getReceiveHistoryColumnPresets(t), [t]);
  const rowActions = useMemo(
    () =>
      buildReceiveHistoryRowActions({
        onShowDisposition: setDispositionConsignmentId,
        onAddAttachment: attachmentDialog.open,
        t
      }),
    [attachmentDialog.open, t]
  );

  if (!isValid) {
    return (
      <Alert>
        <AlertDescription>{t('common.errors.invalidParams')}</AlertDescription>
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {t('receive.history.error.loadFailed', 'Failed to load the receive history. Please try again later.')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold text-foreground">{t('receive.history.title', 'Receive History')}</h1>
        <p className="text-sm text-muted-foreground">
          {totalElements} {t('gen.numres.label', 'results found')}
        </p>
      </header>

      <DataTable
        data={tableData}
        columns={columns}
        title={t('receive.history.title', 'Receive History')}
        helpTarget={MANUAL_NODES.RECEIVE_HISTORY}
        tableId="receive-history-table"
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
        sortField={sortBy}
        sortDirection={sortDirection}
        onSort={handleSort}
        actions={rowActions}
        enableFullscreen
        enableColumnCustomization
        columnPresets={columnPresets}
      />

      <ShowDispositionDialog
        open={!!dispositionConsignmentId}
        onOpenChange={(open) => {
          if (!open) setDispositionConsignmentId(null);
        }}
        consignmentId={dispositionConsignmentId}
      />

      <AttachmentUploadModal
        isOpen={attachmentDialog.shipmentId !== null}
        onClose={attachmentDialog.close}
        onAddAttachment={(file, attachmentType, comment) => void attachmentDialog.upload(file, attachmentType, comment)}
        allowedTypes={RECEIVE_ATTACHMENT_TYPES}
      />
    </div>
  );
}
